import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useThemePreference } from '../../hooks/useThemePreference';
import { Dialog } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { Logout as LogoutIcon } from '../ui/icons';
import SidebarHeader from './sidebar/SidebarHeader';
import SidebarMenu from './sidebar/SidebarMenu';
import SidebarActions from './sidebar/SidebarActions';

const DRAWER_WIDTH = 240;
const COLLAPSED_WIDTH = 72;

interface SidebarProps {
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ mobileOpen, onMobileClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { logout } = useAuth();
  const { isDarkMode, toggleTheme } = useThemePreference();

  const [hovered, setHovered] = useState(false);
  const [pinned, setPinned] = useState(() => localStorage.getItem('sidebarPinned') === 'true');
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);

  // Close mobile drawer on route change
  useEffect(() => {
    if (mobileOpen && onMobileClose) {
      onMobileClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const desktopCollapsed = !hovered && !pinned;

  const handleTogglePin = () => {
    const newPinned = !pinned;
    setPinned(newPinned);
    localStorage.setItem('sidebarPinned', newPinned.toString());
  };

  const handleLogout = () => {
    setLogoutConfirmOpen(true);
  };

  const handleConfirmLogout = async () => {
    setLogoutConfirmOpen(false);
    try {
      await logout();
      navigate('/');
    } catch (_e) {}
  };

  const paperBg = isDarkMode ? 'rgba(26, 15, 10, 0.95)' : 'rgba(255, 255, 255, 0.98)';
  const dividerColor = isDarkMode ? 'rgba(255, 107, 53, 0.2)' : 'rgba(0, 0, 0, 0.1)';

  const renderContent = (collapsed: boolean) => (
    <div
      className="h-full flex flex-col"
      style={{ background: isDarkMode ? 'rgba(26, 15, 10, 0.98)' : 'rgba(255, 255, 255, 1)' }}
    >
      <SidebarHeader isDarkMode={isDarkMode} collapsed={collapsed} />
      {/* Divider below header */}
      <div
        style={{
          marginTop: '60px',
          marginBottom: '4px',
          marginLeft: '16px',
          marginRight: '16px',
          height: 0,
          borderTop: `1px solid ${dividerColor}`,
          opacity: !collapsed ? 1 : 0,
          transition: 'opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      />
      <SidebarMenu collapsed={collapsed} />
      <SidebarActions
        collapsed={collapsed}
        pinned={pinned}
        isDarkMode={isDarkMode}
        onToggleTheme={toggleTheme}
        onLogout={handleLogout}
        onTogglePin={handleTogglePin}
      />
    </div>
  );

  return (
    <>
      {/* MOBILE: temporary overlay drawer — hidden on md+ */}
      <div className="md:hidden">
        {/* Backdrop */}
        <div
          className="fixed inset-0 z-[1302] transition-opacity duration-200"
          style={{
            background: 'rgba(0,0,0,0.5)',
            opacity: mobileOpen ? 1 : 0,
            pointerEvents: mobileOpen ? 'auto' : 'none',
          }}
          onClick={onMobileClose}
        />
        {/* Slide panel */}
        <div
          className="fixed top-0 left-0 h-full z-[1303] overflow-x-hidden overflow-y-auto transition-transform duration-300 ease-in-out"
          style={{
            width: DRAWER_WIDTH,
            transform: mobileOpen ? 'translateX(0)' : `translateX(-${DRAWER_WIDTH}px)`,
            background: paperBg,
            backdropFilter: 'blur(20px)',
            borderRight: isDarkMode ? '1px solid rgba(255, 107, 53, 0.2)' : 'none',
          }}
        >
          {renderContent(false)}
        </div>
      </div>

      {/* DESKTOP: permanent collapsible sidebar — hidden on mobile */}
      {/* Spacer participates in flex layout so main content doesn't overlap */}
      <div
        className="hidden md:block shrink-0"
        style={{
          width: desktopCollapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH,
          transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      />
      {/* Fixed panel */}
      <div
        className="hidden md:flex flex-col fixed top-0 left-0 h-full z-[1302] overflow-x-hidden overflow-y-auto"
        style={{
          width: desktopCollapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH,
          background: paperBg,
          backdropFilter: 'blur(20px)',
          boxShadow: isDarkMode
            ? '4px 0 24px -4px rgba(0,0,0,0.4)'
            : '4px 0 24px -4px rgba(0,0,0,0.15)',
          transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
        onMouseEnter={() => !pinned && setHovered(true)}
        onMouseLeave={() => !pinned && setHovered(false)}
      >
        {renderContent(desktopCollapsed)}
      </div>

      {/* Logout confirmation */}
      <Dialog
        open={logoutConfirmOpen}
        onClose={() => setLogoutConfirmOpen(false)}
        size="sm"
        title={t('menu.logout_confirm_title')}
        description={t('menu.logout_confirm_message')}
        icon={<LogoutIcon size={22} />}
        footer={
          <>
            <Button variant="ghost" onClick={() => setLogoutConfirmOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleConfirmLogout}>{t('menu.logout')}</Button>
          </>
        }
      />
    </>
  );
};

export default Sidebar;
